import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Clock } from 'lucide-react';
import { SEO } from '../components/SEO';
import { blogArticles } from '../data/blogData';

export const BlogList = () => {
  return (
    <div className="pt-16" data-testid="blog-page">
      <SEO
        title="Blog - PDF & AI Resources"
        description="Guides, tutorials, and insights on PDF tools, document automation, and AI-powered workflows. Learn how to work smarter with your documents."
        canonical="/blog"
      />

      {/* Header */}
      <section className="py-20 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p
              className="text-xs tracking-[0.2em] uppercase font-semibold text-teal-700 mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Blog
            </p>
            <h1
              className="text-4xl sm:text-5xl tracking-tight leading-none font-semibold text-slate-900 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              PDF &amp; AI Resources
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Practical guides on working with PDFs, converting documents, and using AI to automate your document workflows. Written in plain language with real-world examples.
            </p>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/blog/${article.slug}`}
                className="group bg-white border border-slate-200/60 rounded-xl p-6 hover:shadow-lg hover:border-teal-200 transition-all duration-200"
                data-testid={`blog-card-${article.slug}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center group-hover:bg-teal-100 transition-colors">
                    <BookOpen className="w-5 h-5 text-teal-600" />
                  </div>
                  <span className="text-xs font-medium text-teal-700 bg-teal-50 px-2.5 py-1 rounded-full">
                    {article.category}
                  </span>
                </div>
                <h2
                  className="text-xl font-medium text-slate-900 mb-2 group-hover:text-teal-700 transition-colors"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {article.title}
                </h2>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">
                  {article.description}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs text-slate-400">
                    <Clock className="w-3.5 h-3.5" />
                    <span>{article.readTime}</span>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-600 group-hover:text-teal-700">
                    Read article
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-900 mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Ready to Try Our Tools?
          </h2>
          <p className="text-slate-600 mb-8">
            Merge, split, compress, and convert PDFs for free. No signup required.
          </p>
          <a
            href="https://pdf.turcoazapps.com"
            className="inline-flex items-center gap-2 bg-slate-900 text-white px-8 py-3.5 rounded-md text-base font-medium hover:bg-slate-800 transition-colors"
            data-testid="blog-cta-button"
          >
            Open Smart PDF Tools
          </a>
        </div>
      </section>
    </div>
  );
};
